import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const ChecklistRunner = ({ checklist, onBack, currentUser }) => {
  const [checklistState, setChecklistState] = useState(() => {
    // Initialize all items as unselected (null = no selection, true = Yes, false = No)
    return checklist.items.map(item => ({
      ...item,
      checked: null // null = no selection, true = Yes, false = No
    }));
  });
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleItemCheck = (index, checked) => {
    setChecklistState(prev => prev.map((item, i) => 
      i === index ? { ...item, checked } : item
    ));
  };

  // Get items marked as "No" (checked === false)
  const noItems = checklistState.filter(item => item.checked === false);
  // Get items marked as "Yes" (checked === true)
  const yesItems = checklistState.filter(item => item.checked === true);
  // Get items with no selection (checked === null)
  const unselectedItems = checklistState.filter(item => item.checked === null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">{checklist.title}</h1>
          <div></div>
        </div>

        {/* Checklist Info */}
        <div className="mb-6 bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-medium text-gray-700">{checklist.category}</p>
              {checklist.description && (
                <p className="text-gray-600 mt-1">{checklist.description}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{checklistState.length} total items</p>
              <p className="text-sm font-medium">
                <span className="text-green-600">{yesItems.length} Yes</span> | 
                <span className="text-red-600 ml-1">{noItems.length} No</span> |
                <span className="text-gray-600 ml-1">{unselectedItems.length} Unselected</span>
              </p>
            </div>
          </div>
        </div>

        {/* Checklist Items */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 mb-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Checklist Items</h2>
          
          <div className="space-y-4">
            {checklistState.map((item, index) => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  {/* Item Info */}
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 mb-1">{item.title}</h3>
                    {item.description && (
                      <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                    )}
                    
                    {/* Yes/No Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleItemCheck(index, true)}
                        className={`px-6 py-2 rounded-lg font-bold transition-colors ${
                          item.checked === true
                            ? 'bg-green-600 text-white shadow-md' 
                            : 'bg-gray-100 text-gray-600 hover:bg-green-100'
                        }`}
                      >
                        ✅ YES
                      </button>
                      <button
                        onClick={() => handleItemCheck(index, false)}
                        className={`px-6 py-2 rounded-lg font-bold transition-colors ${
                          item.checked === false
                            ? 'bg-red-600 text-white shadow-md' 
                            : 'bg-gray-100 text-gray-600 hover:bg-red-100'
                        }`}
                      >
                        ❌ NO
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary of "No" Items - Only show if there are items marked as No */}
        {noItems.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-md mb-6">
            <h3 className="text-xl font-bold text-red-800 mb-4">
              ⚠️ Items Marked as "NO" ({noItems.length})
            </h3>
            <div className="space-y-3">
              {noItems.map((item, index) => (
                <div key={item.id} className="bg-white rounded-lg p-4 border border-red-200">
                  <h4 className="font-medium text-red-700">{item.title}</h4>
                  {item.description && (
                    <p className="text-red-600 text-sm mt-1">{item.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Complete Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-md mb-6">
          <h3 className="text-xl font-bold text-blue-800 mb-4">📊 Checklist Progress</h3>
          <div className="grid md:grid-cols-4 gap-4 text-center">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">{checklistState.length}</div>
              <div className="text-sm text-gray-600">Total Items</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="text-2xl font-bold text-green-600">{yesItems.length}</div>
              <div className="text-sm text-gray-600">YES Items</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="text-2xl font-bold text-red-600">{noItems.length}</div>
              <div className="text-sm text-gray-600">NO Items</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-gray-600">{unselectedItems.length}</div>
              <div className="text-sm text-gray-600">Unselected</div>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progress</span>
              <span>{Math.round(((yesItems.length + noItems.length) / checklistState.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((yesItems.length + noItems.length) / checklistState.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => setShowEmailModal(true)}
            disabled={yesItems.length === 0 && noItems.length === 0}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg"
          >
            📧 EMAIL RESULTS
          </button>
          <button
            onClick={() => {
              const totalSelected = yesItems.length + noItems.length;
              if (totalSelected === 0) {
                alert('Please make selections before marking as complete.');
                return;
              }
              alert(`Checklist completed!\n\n✅ YES: ${yesItems.length} items\n❌ NO: ${noItems.length} items\n⏳ Unselected: ${unselectedItems.length} items`);
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg"
          >
            ✅ MARK COMPLETE
          </button>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <EmailChecklistModal
          checklist={checklist}
          checklistState={checklistState}
          onClose={() => setShowEmailModal(false)}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};
// Simple Create Checklist Modal
const CreateChecklistModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: undefined, // will be filled after first save
    title: '',
    category: 'Tools',
    items: [{ title: '' }]
  });
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const categoryOptions = [
    'Tools',
    'Materials', 
    'Safety',
    'Vehicle',
    'Equipment',
    'Other'
  ];

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { title: '' }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateItem = (index, title) => {
    const updatedItems = formData.items.map((item, i) =>
      i === index ? { ...item, title } : item
    );
    setFormData({ ...formData, items: updatedItems });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const newItems = [...formData.items];
    const draggedItem = newItems[draggedIndex];
    
    // Remove dragged item
    newItems.splice(draggedIndex, 1);
    
    // Insert at new position
    newItems.splice(dropIndex, 0, draggedItem);
    
    setFormData({ ...formData, items: newItems });
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      
      // If we're on the last item, add a new one
      if (index === formData.items.length - 1) {
        addItem();
        // Focus the new item after a brief delay
        setTimeout(() => {
          const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
          if (nextInput) {
            nextInput.focus();
          }
        }, 10);
      } else {
        // Focus the next item
        const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.title.trim()) {
      alert('Please enter a checklist title.');
      return;
    }

    const validItems = formData.items.filter(item => item.title.trim() !== '');
    if (validItems.length === 0) {
      alert('Please add at least one checklist item.');
      return;
    }

    setLoading(true);
    try {
      // Convert to backend format
      const checklistData = {
        title: formData.title.trim(),
        category: formData.category,
        description: '', // No description needed
        items: validItems.map(item => ({
          title: item.title.trim(),
          description: '', // No description needed
          is_required: false,
          completed: false,
          notes: '',
          priority: 'normal'
        }))
      };

      await axios.post(`${API}/checklists`, checklistData);
      alert(`✅ Checklist "${formData.title}" created successfully with ${validItems.length} items!`);
      onSuccess();
    } catch (error) {
      console.error('Error creating checklist:', error);
      if (error.response?.status === 403) {
        alert('❌ Access denied. Only administrators can create checklists.');
      } else if (error.response?.status === 409) {
        alert('❌ A checklist with this title already exists in this category.');
      } else {
        alert('❌ Error creating checklist. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Create New Checklist</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Checklist Title */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Checklist Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Daily Tool Checklist"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              {categoryOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Checklist Items */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Checklist Items * (Use Tab to add new items quickly, Drag & Drop to reorder)
            </label>
            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 p-2 rounded-lg transition-all duration-200 ${
                    draggedIndex === index 
                      ? 'opacity-50 transform scale-105 bg-blue-100 border-2 border-blue-300' 
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  } cursor-move`}
                >
                  {/* Drag Handle */}
                  <div className="text-gray-400 cursor-move select-none p-1">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <circle cx="3" cy="3" r="1"/>
                      <circle cx="3" cy="8" r="1"/>
                      <circle cx="3" cy="13" r="1"/>
                      <circle cx="8" cy="3" r="1"/>
                      <circle cx="8" cy="8" r="1"/>
                      <circle cx="8" cy="13" r="1"/>
                    </svg>
                  </div>
                  
                  <span className="text-gray-500 font-bold w-8">{index + 1}.</span>
                  
                  <input
                    type="text"
                    data-index={index}
                    value={item.title}
                    onChange={(e) => updateItem(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className="flex-1 p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Item name (e.g., Safety glasses)"
                    autoFocus={index === formData.items.length - 1 && formData.items.length > 1}
                  />
                  
                  {/* Remove button */}
                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center"
                      title="Remove item"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={addItem}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold"
              >
                + Add Item
              </button>
              <p className="text-sm text-gray-500 py-2">
                Tip: Press Tab to add new items, Drag & Drop items to reorder
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-4 rounded-xl font-bold text-white text-lg"
            >
              {loading ? 'Creating...' : 'Create Checklist'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 py-4 rounded-xl font-bold text-white text-lg"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};

// Email Checklist Modal
const EmailChecklistModal = ({ checklist, checklistState, onClose, currentUser }) => {
  const [emailTo, setEmailTo] = useState('');
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [sending, setSending] = useState(false);

  const yesItems = checklistState.filter(item => item.checked === true);
  const noItems = checklistState.filter(item => item.checked === false);
  const unselectedItems = checklistState.filter(item => item.checked === null);

  const generateEmailContent = () => {
    const timestamp = new Date().toLocaleString();
    const completedBy = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User';
    
    let content = `CHECKLIST RESULTS - ${checklist.title}\n`;
    content += `Category: ${checklist.category}\n`;
    content += `Completed by: ${completedBy}\n`;
    content += `Date/Time: ${timestamp}\n\n`;
    
    if (checklist.description) {
      content += `Description: ${checklist.description}\n\n`;
    }
    
    content += `SUMMARY:\n`;
    content += `- Total Items: ${checklistState.length}\n`;
    content += `- YES Items: ${yesItems.length}\n`;
    content += `- NO Items: ${noItems.length}\n`;
    content += `- Unselected Items: ${unselectedItems.length}\n`;
    
    const completionPercentage = Math.round(((yesItems.length + noItems.length) / checklistState.length) * 100);
    content += `- Completion: ${completionPercentage}%\n\n`;
    
    if (yesItems.length > 0) {
      content += `YES ITEMS (${yesItems.length}):\n`;
      yesItems.forEach((item, index) => {
        content += `${index + 1}. ✅ ${item.title}\n`;
        if (includeDescriptions && item.description) {
          content += `   Description: ${item.description}\n`;
        }
      });
      content += `\n`;
    }
    
    if (noItems.length > 0) {
      content += `NO ITEMS - REQUIRE ATTENTION (${noItems.length}):\n`;
      noItems.forEach((item, index) => {
        content += `${index + 1}. ❌ ${item.title}\n`;
        if (includeDescriptions && item.description) {
          content += `   Description: ${item.description}\n`;
        }
      });
      content += `\n`;
    }
    
    if (unselectedItems.length > 0) {
      content += `UNSELECTED ITEMS (${unselectedItems.length}):\n`;
      unselectedItems.forEach((item, index) => {
        content += `${index + 1}. ⏳ ${item.title}\n`;
        if (includeDescriptions && item.description) {
          content += `   Description: ${item.description}\n`;
        }
      });
      content += `\n`;
    }
    
    content += `---\nGenerated by Checklist System`;
    
    return content;
  };

  const handleSendEmail = () => {
    if (!emailTo.trim()) {
      alert('Please enter an email address.');
      return;
    }

    setSending(true);
    
    try {
      const emailContent = generateEmailContent();
      const subject = encodeURIComponent(`Checklist Results: ${checklist.title}`);
      const body = encodeURIComponent(emailContent);
      const mailtoLink = `mailto:${emailTo}?subject=${subject}&body=${body}`;
      
      window.open(mailtoLink);
      
      setTimeout(() => {
        setSending(false);
        alert('📧 Email client opened! The checklist results have been prepared for sending.');
        onClose();
      }, 1000);
      
    } catch (error) {
      console.error('Error preparing email:', error);
      alert('❌ Error preparing email. Please try again.');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">📧 Email Checklist Results</h2>
        
        <div className="space-y-6">
          {/* Email Address */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Send To Email Address *
            </label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address..."
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              You can also send to yourself: {currentUser?.email || 'your-email@company.com'}
            </p>
          </div>

          {/* Options */}
          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={includeDescriptions}
                onChange={(e) => setIncludeDescriptions(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-gray-700 font-medium">
                Include item descriptions
              </span>
            </label>
          </div>

          {/* Preview */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-2">Email Preview:</h3>
            <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
              {generateEmailContent().substring(0, 400)}...
            </div>
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-blue-800 mb-2">Summary to be emailed:</h3>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-blue-600">{checklistState.length}</div>
                <div className="text-sm text-gray-600">Total Items</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-600">{yesItems.length}</div>
                <div className="text-sm text-gray-600">YES Items</div>
              </div>
              <div>
                <div className="text-lg font-bold text-red-600">{noItems.length}</div>
                <div className="text-sm text-gray-600">NO Items</div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-600">{unselectedItems.length}</div>
                <div className="text-sm text-gray-600">Unselected</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              onClick={handleSendEmail}
              disabled={sending || !emailTo.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-4 rounded-xl font-bold text-white text-lg"
            >
              {sending ? 'Opening Email...' : '📧 Send Email'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 py-4 rounded-xl font-bold text-white text-lg"
            >
              Cancel
            </button>
          </div>
        </div>
        </div>
      </div>
  );
};
// Simple Edit Checklist Modal
const EditChecklistModal = ({ checklist, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: checklist?.title || '',
    category: checklist?.category || 'Tools',
    items: checklist?.items?.map(item => ({ title: item.title || '' })) || [{ title: '' }]
  });
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const categoryOptions = [
    'Tools',
    'Materials', 
    'Safety',
    'Vehicle',
    'Equipment',
    'Other'
  ];

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { title: '' }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateItem = (index, title) => {
    const updatedItems = formData.items.map((item, i) =>
      i === index ? { ...item, title } : item
    );
    setFormData({ ...formData, items: updatedItems });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const newItems = [...formData.items];
    const draggedItem = newItems[draggedIndex];
    
    // Remove dragged item
    newItems.splice(draggedIndex, 1);
    
    // Insert at new position
    newItems.splice(dropIndex, 0, draggedItem);
    
    setFormData({ ...formData, items: newItems });
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      
      // If we're on the last item, add a new one
      if (index === formData.items.length - 1) {
        addItem();
        // Focus the new item after a brief delay
        setTimeout(() => {
          const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
          if (nextInput) {
            nextInput.focus();
          }
        }, 10);
      } else {
        // Focus the next item
        const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.title.trim()) {
      alert('Please enter a checklist title.');
      return;
    }

    const validItems = formData.items.filter(item => item.title.trim() !== '');
    if (validItems.length === 0) {
      alert('Please add at least one checklist item.');
      return;
    }

    setLoading(true);
    try {
      // Convert to backend format
      const updateData = {
        title: formData.title.trim(),
        category: formData.category,
        description: '', // No description needed
        items: validItems.map(item => ({
          id: `item-${Date.now()}-${Math.random()}`,
          title: item.title.trim(),
          description: '', // No description needed
          is_required: false,
          completed: false,
          notes: '',
          priority: 'normal'
        }))
      };

      await axios.put(`${API}/checklists/${checklist.id}`, updateData);
      alert(`✅ Checklist "${formData.title}" updated successfully with ${validItems.length} items!`);
      onSuccess();
    } catch (error) {
      console.error('Error updating checklist:', error);
      if (error.response?.status === 403) {
        alert('❌ Access denied. Only administrators can edit checklists.');
      } else if (error.response?.status === 404) {
        alert('❌ Checklist not found. It may have been deleted.');
      } else if (error.response?.status === 409) {
        alert('❌ A checklist with this title already exists in this category.');
      } else {
        alert('❌ Error updating checklist. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Edit Checklist</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Checklist Title */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Checklist Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Daily Tool Checklist"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              {categoryOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Checklist Items */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Checklist Items * (Use Tab to add new items quickly, Drag & Drop to reorder)
            </label>
            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 p-2 rounded-lg transition-all duration-200 ${
                    draggedIndex === index 
                      ? 'opacity-50 transform scale-105 bg-blue-100 border-2 border-blue-300' 
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  } cursor-move`}
                >
                  {/* Drag Handle */}
                  <div className="text-gray-400 cursor-move select-none p-1">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <circle cx="3" cy="3" r="1"/>
                      <circle cx="3" cy="8" r="1"/>
                      <circle cx="3" cy="13" r="1"/>
                      <circle cx="8" cy="3" r="1"/>
                      <circle cx="8" cy="8" r="1"/>
                      <circle cx="8" cy="13" r="1"/>
                    </svg>
                  </div>
                  
                  <span className="text-gray-500 font-bold w-8">{index + 1}.</span>
                  
                  <input
                    type="text"
                    data-index={index}
                    value={item.title}
                    onChange={(e) => updateItem(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className="flex-1 p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Item name (e.g., Safety glasses)"
                  />
                  
                  {/* Remove button */}
                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center"
                      title="Remove item"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={addItem}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold"
              >
                + Add Item
              </button>
              <p className="text-sm text-gray-500 py-2">
                Tip: Press Tab to add new items, Drag & Drop items to reorder
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-4 rounded-xl font-bold text-white text-lg"
            >
              {loading ? 'Updating...' : 'Update Checklist'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 py-4 rounded-xl font-bold text-white text-lg"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};

// Simple Checklists Module - Easy to use for crews
const ChecklistsModule = ({ onBack, currentUser }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API}/checklists`);
      setChecklists(response.data);
    } catch (error) {
      console.error('Error fetching checklists:', error);
      setError('Failed to load checklists. Please try again.');
      
      // Try to initialize if no checklists found
      try {
        await axios.post(`${API}/checklists/initialize`);
        const response = await axios.get(`${API}/checklists`);
        setChecklists(response.data);
        setError('');
      } catch (initError) {
        console.error('Error initializing checklists:', initError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (checklist) => {
    console.log('🗑️ Delete function called for checklist:', checklist.title);
    console.log('🔑 Current user:', currentUser);
    console.log('🔑 User role:', currentUser?.rawRole);
    
    if (!currentUser || currentUser.rawRole !== 'admin') {
      alert('❌ Only administrators can delete checklists.');
      return;
    }

    const confirmDelete = window.confirm(
      `⚠️ Are you sure you want to delete "${checklist.title}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      console.log('🌐 Making DELETE request to:', `${API}/checklists/${checklist.id}`);
      console.log('🔗 API base URL:', API);
      console.log('📝 Checklist ID:', checklist.id);
      
      const response = await axios.delete(`${API}/checklists/${checklist.id}`);
      console.log('✅ Delete response:', response);
      
      alert(`✅ Checklist "${checklist.title}" deleted successfully!`);
      fetchChecklists();
    } catch (error) {
      console.error('❌ Delete error details:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      
      if (error.response?.status === 401) {
        alert('❌ Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        alert('❌ Access denied. Only administrators can delete checklists.');
      } else if (error.response?.status === 404) {
        alert('❌ Checklist not found. It may have already been deleted.');
      } else if (error.response?.status === 500) {
        alert(`❌ Server error: ${error.response?.data?.detail || 'Internal server error'}`);
      } else {
        alert(`❌ Error deleting checklist: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      }
    }
  };

  // Filter checklists based on search query
  const filteredChecklists = checklists.filter(checklist => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      checklist.title.toLowerCase().includes(query) ||
      checklist.category.toLowerCase().includes(query) ||
      checklist.description?.toLowerCase().includes(query) ||
      checklist.items.some(item => 
        item.title.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      )
    );
  });

  // If viewing/using a checklist
  if (activeChecklist) {
    return (
      <ChecklistRunner 
        checklist={activeChecklist}
        onBack={() => setActiveChecklist(null)}
        currentUser={currentUser}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">CHECKLISTS</h1>
          {currentUser && currentUser.rawRole === 'admin' ? (
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded-xl font-bold text-white shadow-lg"
            >
              + CREATE CHECKLIST
            </button>
          ) : (
            <div></div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Universal Search */}
        <div className="mb-8">
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="text-2xl">🔍</div>
              <input
                type="text"
                placeholder="Search checklists by name, category, or items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 p-4 text-lg rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-gray-500 hover:text-gray-700 text-xl px-2"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="mt-3 text-sm text-gray-600">
                Found {filteredChecklists.length} checklist{filteredChecklists.length !== 1 ? 's' : ''} matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4 animate-pulse">📋</div>
            <p className="text-xl text-gray-600">Loading checklists...</p>
          </div>
        ) : (
          <>
            {/* Checklists Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredChecklists.map((checklist) => (
                <div
                  key={checklist.id}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 relative"
                >
                  {/* Admin Action Buttons */}
                  {currentUser && currentUser.rawRole === 'admin' && (
                    <div className="absolute top-3 right-3 flex gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingChecklist(checklist);
                          setShowEditModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm shadow-lg"
                        title="Edit Checklist"
                      >
                        ✏️
                      </button>
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(checklist);
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm shadow-lg"
                        title="Delete Checklist"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                  
                  <div 
                    className="cursor-pointer"
                    onClick={() => setActiveChecklist(checklist)}
                  >
                    <div className="text-center mb-4">
                      <div className="text-4xl mb-3">✅</div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{checklist.title}</h3>
                      <p className="text-blue-600 text-sm font-medium mb-2">{checklist.category}</p>
                      {checklist.description && (
                        <p className="text-gray-600 text-sm mb-3">{checklist.description}</p>
                      )}
                    </div>
                    
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>{checklist.items.length} items</span>
                        {checklist.estimated_duration && (
                          <span>~{checklist.estimated_duration} min</span>
                        )}
                      </div>
                      
                      <div className="mt-3">
                        <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-bold transition-colors">
                          START CHECKLIST
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Search match indicator */}
                  {searchQuery && (
                    <div className="absolute top-3 left-3">
                      <span className="bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-bold">
                        MATCH
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredChecklists.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">📋</div>
                <p className="text-xl mb-2">
                  {searchQuery ? 'No checklists found' : 'No checklists available'}
                </p>
                {searchQuery ? (
                  <div>
                    <p className="mb-4">No checklists match your search for "{searchQuery}"</p>
                    <button
                      onClick={() => setSearchQuery('')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-bold"
                    >
                      Clear Search
                    </button>
                  </div>
                ) : (
                  <p>Create your first checklist to get started</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Create Checklist Modal */}
      {showCreateModal && (
        <CreateChecklistModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchChecklists();
          }}
        />
      )}
      
      {/* Edit Checklist Modal */}
      {showEditModal && editingChecklist && (
        <EditChecklistModal 
          checklist={editingChecklist}
          onClose={() => {
            setShowEditModal(false);
            setEditingChecklist(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingChecklist(null);
            fetchChecklists();
          }}
        />
      )}
    </div>
  );
};

// Employee Contacts Module (Simplified for Production - Contact Info Only)

export default ChecklistsModule;
